import { runInAction } from "mobx"

import type { IGaugeRepository } from "@/domains/gauge"
import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import type { TokenRepository } from "@/domains/tokens"
import type { WalletConnectionStore } from "@/domains/wallet"
import { LcAddress } from "@/domains/core"
import { IEventBus } from "@/domains/events"
import { GaugeAddedToRepositoryEvent } from "@/domains/gauge/events"
import { OrchestratedQuery } from "@/domains/library"
import { MinimalPoolApiTransport } from "@/domains/minimal-pool/minimal-pool-api-transport"
import { convertApiResponseToMinimalPoolStoreDAO } from "@/domains/minimal-pool/minimal-pool-converter"
import { MinimalPoolRepository } from "@/domains/minimal-pool/minimal-pool-repository"
import { MinimalPoolStore } from "@/domains/minimal-pool/minimal-pool-store"
import { MinimalPoolApiData } from "@/domains/minimal-pool/types"
import { MinimalPoolApiFactory } from "@/domains/tokens/factories/minimal-pool-api-factory"
import { WalletConnectedEvent, WalletDisconnectedEvent } from "@/domains/wallet/events"

import type { MinimalPoolOnChainTransport } from "./minimal-pool-on-chain-transport"
import { DefaultInfoPairValues } from "../on-chain/scalens/frontend-default-info-pairs"
import { ScalensUserInfoPairs } from "../on-chain/scalens/frontend-user-info-pairs"
import { ScalensUserManageMinimalPoolInfo } from "../on-chain/scalens/frontend-user-manage-minimal-pool"

export class MinimalPoolOrchestrator {
  private loadPoolsQuery: OrchestratedQuery<Array<MinimalPoolApiData>> | undefined
  private infoPairsQuery: OrchestratedQuery<any[]> | undefined
  private userInfoPairsQuery: OrchestratedQuery<any[]> | undefined
  private userManageMinimalPoolInfoQuery: OrchestratedQuery<any> | undefined
  private currentManagedPool: MinimalPoolStore | undefined
  private walletConnectedHandler: ((event: WalletConnectedEvent) => void) | undefined
  private walletDisconnectedHandler: ((event: WalletDisconnectedEvent) => void) | undefined

  private gaugeAddedHandler: ((event: GaugeAddedToRepositoryEvent) => void) | undefined

  constructor(
    private readonly minimalPoolRepository: MinimalPoolRepository,
    private readonly minimalPoolApiTransport: MinimalPoolApiTransport,
    private readonly tokenRepository: TokenRepository,
    private readonly routerAddress: LcAddress,
    private readonly eventBus: IEventBus,
    private readonly gaugeRepository: IGaugeRepository | undefined,
    private readonly bannedPairAddresses: ReadonlySet<string>,
  ) {}

  initialize(
    loadPoolsQuery: OrchestratedQuery<Array<MinimalPoolApiData>>,
    infoPairsQuery?: OrchestratedQuery<any[]>,
    userInfoPairsQuery?: OrchestratedQuery<any[]>,
    userManageMinimalPoolInfoQuery?: OrchestratedQuery<ScalensUserManageMinimalPoolInfo | undefined>,
  ) {
    this.loadPoolsQuery = loadPoolsQuery
    this.infoPairsQuery = infoPairsQuery
    this.userInfoPairsQuery = userInfoPairsQuery
    this.userManageMinimalPoolInfoQuery = userManageMinimalPoolInfoQuery

    // Subscribe to wallet events
    this.walletConnectedHandler = this.handleWalletConnect.bind(this)
    this.walletDisconnectedHandler = this.handleWalletDisconnect.bind(this)
    this.eventBus.subscribe(WalletConnectedEvent.TYPE, this.walletConnectedHandler)
    this.eventBus.subscribe(WalletDisconnectedEvent.TYPE, this.walletDisconnectedHandler)

    // Subscribe to gauge added events to attempt pool connections
    if (this.gaugeRepository) {
      this.gaugeAddedHandler = this.handleGaugeAdded.bind(this)
      this.eventBus.subscribe(GaugeAddedToRepositoryEvent.TYPE, this.gaugeAddedHandler)
    }
  }

  private handleWalletDisconnect(event: WalletDisconnectedEvent): void {
    if (this.userInfoPairsQuery) {
      this.userInfoPairsQuery.stop()
    }
    // Don't stop userManageMinimalPoolInfoQuery - it falls back to safe address
    // Just force re-execution to pick up the new who address (safe address)
    if (this.userManageMinimalPoolInfoQuery && this.currentManagedPool) {
      this.userManageMinimalPoolInfoQuery.forceExecute()
    }
    this.minimalPoolRepository.clearAllUserValues()
  }

  private async handleWalletConnect(event: WalletConnectedEvent): Promise<void> {
    if (this.userInfoPairsQuery) {
      this.userInfoPairsQuery.start()
    }
    // Force re-execution of userManageMinimalPoolInfoQuery to pick up connected wallet address
    if (this.userManageMinimalPoolInfoQuery && this.currentManagedPool) {
      this.userManageMinimalPoolInfoQuery.forceExecute()
    }
  }

  get query(): OrchestratedQuery<Array<MinimalPoolApiData>> | undefined {
    return this.loadPoolsQuery
  }

  get infoPairsQueryInstance(): OrchestratedQuery<any[]> | undefined {
    return this.infoPairsQuery
  }

  get userInfoPairsQueryInstance(): OrchestratedQuery<ScalensUserInfoPairs[]> | undefined {
    return this.userInfoPairsQuery
  }

  get userManagePoolQueryInstance(): OrchestratedQuery<ScalensUserManageMinimalPoolInfo | undefined> | undefined {
    return this.userManageMinimalPoolInfoQuery
  }

  /**
   * Load pools from API (triggered by OrchestratedQuery)
   * This just fetches the data and returns it for the query system
   */
  async onLoadPoolsTrigger(
    _query: IOrchestratedQuerySender<Array<MinimalPoolApiData>>,
  ): Promise<Array<MinimalPoolApiData>> {
    return await this.minimalPoolApiTransport.loadMinimalPools()
  }

  /**
   * Callback for when pools query succeeds
   */
  onLoadPoolsQuerySuccess(
    _sender: IOrchestratedQuerySender<Array<MinimalPoolApiData>>,
    data: Array<MinimalPoolApiData>,
  ): void {
    try {
      runInAction(() => {
        for (let i = 0; i < data.length; i++) {
          const element = data[i]

          if (element) {
            try {
              this.processPoolFromAPI(element)
            } catch (error) {
              console.error("Error processing pool data:", element, error)
            }
          }
        }
      })

      // we should force the info pairs query to run
      // do NOT put inside the `runInAction` we want that to complete and render asap
      if (true) {
        try {
          this?.infoPairsQuery?.forceExecute()
        } catch (error) {
          console.error("Failed to force info pairs query to run", error)
        }
      }
    } catch (error) {
      console.error("Error processing minimal pool data:", error)
    }
  }

  /**
   * Callback for when pools query fails
   */
  onLoadPoolsQueryFailed(_sender: IOrchestratedQuerySender<Array<MinimalPoolApiData>>, error: Error): void {
    console.error("Failed to load minimal pools", error)
  }

  async onLoadUserInfoPairsTrigger(
    query: IOrchestratedQuerySender<Array<any>>,
    minimalPoolOnChainTransport: MinimalPoolOnChainTransport,
    walletStore: WalletConnectionStore,
  ): Promise<Array<any>> {
    // If no connected address, use safe address, but ensure it's valid
    const safeAddress = walletStore.safeLensAddress
    if (!walletStore.connectedAddress && (!safeAddress || typeof safeAddress !== "string")) {
      // Return empty array if we don't have a valid address to query
      return []
    }
    const who = walletStore.connectedAddress ?? new LcAddress(safeAddress)
    const pools = this.minimalPoolRepository.getAll()
    const userInfoPairs = await minimalPoolOnChainTransport.loadMinimalPoolUserInfoPairs(who, pools, query, 100)
    return userInfoPairs
  }

  onLoadUserInfoPairsQuerySuccess(
    _sender: IOrchestratedQuerySender<ScalensUserInfoPairs[]>,
    data: Array<ScalensUserInfoPairs>,
  ): void {
    try {
      runInAction(() => {
        data.forEach((userInfoPair) => {
          this.processUserInfoPairFromOnChain(userInfoPair)
        })
      })
    } catch (error) {
      console.error("Error processing user info pairs data:", error)
    }
  }

  onLoadUserInfoPairsQueryFailed(_sender: IOrchestratedQuerySender<any[]>, error: Error): void {
    console.error("❌ MinimalPoolOrchestrator: Failed to load user info pairs", error)
  }
  /**
   * Load info pairs from on-chain (triggered by OrchestratedQuery)
   * This just fetches the data and returns it for the query system
   */
  async onLoadInfoPairsTrigger(
    query: IOrchestratedQuerySender<Array<DefaultInfoPairValues>>,
    minimalPoolOnChainTransport: MinimalPoolOnChainTransport,
  ): Promise<Array<DefaultInfoPairValues>> {
    const pools = this.minimalPoolRepository.getAll()

    const infoPairs = await minimalPoolOnChainTransport.loadMinimalPoolInfoPairs(pools, query, 70)

    return infoPairs
  }

  /**
   * Callback for when info pairs query succeeds
   */
  onLoadInfoPairsQuerySuccess(_sender: IOrchestratedQuerySender<any[]>, data: Array<DefaultInfoPairValues>): void {
    try {
      runInAction(() => {
        data.forEach((infoPair) => {
          const pool = this.minimalPoolRepository.find(infoPair.address)
          if (pool) {
            pool.updateInfoPairValues(infoPair)
          }
        })
      })
    } catch (error) {
      console.error("Error processing info pairs data:", error)
    }
  }

  /**
   * Callback for when info pairs query fails
   */
  onLoadInfoPairsQueryFailed(_sender: IOrchestratedQuerySender<any[]>, error: Error): void {
    console.error("❌ MinimalPoolOrchestrator: Failed to load info pairs", error)
  }

  private processUserInfoPairFromOnChain(userInfoPair: ScalensUserInfoPairs): void {
    const found = this.minimalPoolRepository.find(userInfoPair.pairAddress)
    if (found) {
      found.updateScalensUserInfoPairValues(userInfoPair)
    }
  }

  /**
   * Process pool data from API and add to repository
   */
  private processPoolFromAPI(apiData: MinimalPoolApiData): void {
    const address = new LcAddress(apiData.address)
    if (this.bannedPairAddresses.has(address.toString())) {
      return
    }

    const found = this.minimalPoolRepository.find(address)
    if (found) {
      found.updateStaticData(apiData)
      return
    }

    // Ensure tokens exist in repository
    let token0 = this.tokenRepository.find(new LcAddress(apiData.token0.address))
    if (!token0) {
      token0 = MinimalPoolApiFactory.createToken({
        address: new LcAddress(apiData.token0.address),
        symbol: apiData.token0.symbol,
        decimals: apiData.token0.decimals,
        priceUsd: apiData?.token0?.priceUsd ?? 0,
      })
      this.tokenRepository.add(token0)
    }

    let token1 = this.tokenRepository.find(new LcAddress(apiData.token1.address))
    if (!token1) {
      token1 = MinimalPoolApiFactory.createToken({
        address: new LcAddress(apiData.token1.address),
        symbol: apiData.token1.symbol,
        decimals: apiData.token1.decimals,
        priceUsd: apiData?.token1?.priceUsd ?? 0,
      })
      this.tokenRepository.add(token1)
    }

    // Convert API data directly to store DAO
    const poolStoreDAO = convertApiResponseToMinimalPoolStoreDAO(apiData, token0, token1)

    // Create new pool
    const pool = new MinimalPoolStore(poolStoreDAO)

    this.minimalPoolRepository.add(pool)

    // Attempt to connect to gauge if gauge address exists
    if (pool.gaugeAddress && this.gaugeRepository) {
      this.attemptPoolToGaugeConnection(pool)
    }
  }

  private handleGaugeAdded(event: GaugeAddedToRepositoryEvent): void {
    // When a gauge is added, check if any pools can connect to it
    if (!this.gaugeRepository) {
      return
    }

    const gauge = this.gaugeRepository.find(event.gaugeAddress)
    if (!gauge) {
      return
    }

    // Find pools with matching gauge address
    const pools = this.minimalPoolRepository.getAll()
    for (const pool of pools) {
      if (pool.gaugeAddress?.equals(event.gaugeAddress) && !pool.gauge) {
        this.attemptPoolToGaugeConnection(pool)
      }
    }
  }

  private attemptPoolToGaugeConnection(pool: MinimalPoolStore): void {
    if (!pool.gaugeAddress || !this.gaugeRepository) {
      return
    }

    const gauge = this.gaugeRepository.find(pool.gaugeAddress)
    if (gauge && !pool.gauge) {
      pool.connectToGauge(gauge)
    }
  }

  /**
   * Set background sync interval for pools with specific milliseconds (defaults to 60 seconds)
   */
  setPoolsSyncInterval(intervalMs: number = 60_000): { success: boolean; message?: string } {
    if (this.loadPoolsQuery) {
      this.loadPoolsQuery.changeInterval(intervalMs)
      return { success: true }
    }
    return {
      success: false,
      message: "MinimalPoolOrchestrator: loadPoolsQuery is not initialized, cannot set interval.",
    }
  }

  /**
   * Start background sync for pools with specified interval (defaults to 60 seconds)
   */
  startPoolsSync(intervalMs: number = 60_000): { success: boolean; message?: string } {
    if (this.loadPoolsQuery) {
      this.loadPoolsQuery.changeInterval(intervalMs)
      this.loadPoolsQuery.start()
      return { success: true }
    }
    return { success: false, message: "MinimalPoolOrchestrator: loadPoolsQuery is not initialized, cannot start sync." }
  }

  /**
   * Stop background sync for pools
   */
  stopPoolsSync(): { success: boolean; message?: string } {
    if (this.loadPoolsQuery) {
      this.loadPoolsQuery.stop()
      return { success: true }
    }
    return { success: false, message: "MinimalPoolOrchestrator: loadPoolsQuery is not initialized, cannot stop sync." }
  }

  /**
   * Force immediate pools sync (get fresh data now)
   */
  forcePoolsSync(): { success: boolean; message?: string } {
    if (this.loadPoolsQuery) {
      this.loadPoolsQuery.forceExecute()
      return { success: true }
    }
    return { success: false, message: "MinimalPoolOrchestrator: loadPoolsQuery is not initialized, cannot force sync." }
  }

  // ===== USER MANAGE POOL SYNC =====

  /**
   * Load user manage pool info from on-chain (triggered by OrchestratedQuery)
   */
  async onLoadUserManagePoolTrigger(
    _query: IOrchestratedQuerySender<ScalensUserManageMinimalPoolInfo | undefined>,
    minimalPoolOnChainTransport: MinimalPoolOnChainTransport,
    walletStore: WalletConnectionStore,
  ): Promise<ScalensUserManageMinimalPoolInfo | undefined> {
    if (!this.currentManagedPool) {
      return undefined
    }

    // If no connected address, use safe address, but ensure it's valid
    const safeAddress = walletStore.safeLensAddress
    if (!walletStore.connectedAddress && (!safeAddress || typeof safeAddress !== "string")) {
      // Return undefined if we don't have a valid address to query
      return undefined
    }
    const who = walletStore.connectedAddress ?? new LcAddress(safeAddress)

    // console.log("walletStore.connectedAddress", walletStore.connectedAddress)
    // console.log("walletStore.safeLensAddress", walletStore.safeLensAddress)
    // console.log("who", who)

    return await minimalPoolOnChainTransport.loadUserManageMinimalPoolInfo(
      who,
      this.currentManagedPool,
      this.routerAddress,
    )
  }

  /**
   * Callback for when user manage pool query succeeds
   */
  onLoadUserManagePoolQuerySuccess(
    _sender: IOrchestratedQuerySender<ScalensUserManageMinimalPoolInfo | undefined>,
    data: ScalensUserManageMinimalPoolInfo | undefined,
  ): void {
    try {
      if (data && this.currentManagedPool) {
        runInAction(() => {
          this.currentManagedPool?.updateUserManageValues(data)
        })
      }
    } catch (error) {
      console.error("Error processing user manage pool data:", error)
    }
  }

  /**
   * Callback for when user manage pool query fails
   */
  onLoadUserManagePoolQueryFailed(
    _sender: IOrchestratedQuerySender<ScalensUserManageMinimalPoolInfo | undefined>,
    error: Error,
  ): void {
    console.error("❌ MinimalPoolOrchestrator: Failed to load user manage pool info", error)
  }

  /**
   * Start syncing user manage pool info for a specific pool
   */
  startUserManagePoolSync(pool: MinimalPoolStore, intervalMs: number = 30_000): { success: boolean; message?: string } {
    this.currentManagedPool = pool
    if (this.userManageMinimalPoolInfoQuery) {
      this.userManageMinimalPoolInfoQuery.changeInterval(intervalMs)
      this.userManageMinimalPoolInfoQuery.start()
      return { success: true }
    }
    return {
      success: false,
      message: "MinimalPoolOrchestrator: userManageMinimalPoolInfoQuery is not initialized, cannot start sync.",
    }
  }

  /**
   * Stop syncing user manage pool info
   */
  stopUserManagePoolSync(): { success: boolean; message?: string } {
    this.currentManagedPool = undefined
    if (this.userManageMinimalPoolInfoQuery) {
      this.userManageMinimalPoolInfoQuery.stop()
      return { success: true }
    }
    return {
      success: false,
      message: "MinimalPoolOrchestrator: userManageMinimalPoolInfoQuery is not initialized, cannot stop sync.",
    }
  }

  /**
   * Force immediate user manage pool sync
   */
  forceUserManagePoolSync(): { success: boolean; message?: string } {
    if (this.userManageMinimalPoolInfoQuery) {
      this.userManageMinimalPoolInfoQuery.forceExecute()
      return { success: true }
    }
    return {
      success: false,
      message: "MinimalPoolOrchestrator: userManageMinimalPoolInfoQuery is not initialized, cannot force sync.",
    }
  }

  // ===== DELEGATION TO REPOSITORY =====

  get pools() {
    return this.minimalPoolRepository.pools
  }

  find(address: LcAddress) {
    return this.minimalPoolRepository.find(address)
  }

  getAll() {
    return this.minimalPoolRepository.getAll()
  }

  getSystemPools() {
    return this.minimalPoolRepository.getSystemPools()
  }

  getActivePools() {
    return this.minimalPoolRepository.getActivePools()
  }

  getPoolsByTag(tag: string) {
    return this.minimalPoolRepository.getPoolsByTag(tag)
  }

  get totalPools() {
    return this.minimalPoolRepository.totalPools
  }

  get totalTvlUsd() {
    return this.minimalPoolRepository.totalTvlUsd
  }

  debug() {
    console.log("Debugging")
    console.log(" - Pool count", this.pools.size)
    console.log(" - Repository", this.minimalPoolRepository)
    console.log(" - Load pools query", this.loadPoolsQuery?.executionCount)
    console.log(" - Info pairs query", this.infoPairsQuery?.executionCount)
    console.log(" - Info pairs query", this.infoPairsQuery?.lastResult)
    console.log(" - User info pairs query", this.userInfoPairsQuery?.executionCount)
  }
}
